import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { uploadImage, resolveImageUrl } from "@/lib/api";
import { Loader2, ImageOff, Upload, Camera, X } from "lucide-react";

export default function ImageUpload({ value, onChange, testId = "image-upload", compact = false }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setImgError(false);
      onChange(url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const src = resolveImageUrl(value);

  if (compact) {
    return (
      <div data-testid={testId}>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testId}-file-input`} />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="group relative h-full min-h-[92px] w-20 shrink-0 overflow-hidden rounded-sm border border-slate-200 bg-slate-50 transition-colors hover:border-[#002FA7]"
          data-testid={`${testId}-pick-button`}
        >
          {uploading ? (
            <div className="flex h-full w-full items-center justify-center text-[#002FA7]"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : src && !imgError ? (
            <img src={src} alt="preview" className="h-full w-full object-cover" onError={() => setImgError(true)} data-testid={`${testId}-preview`} />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-300 group-hover:text-[#002FA7]">
              <Upload className="h-4 w-4" />
              <span className="text-[9px] font-medium uppercase tracking-wide">Photo</span>
            </div>
          )}
        </button>
        {src && !uploading && (
          <button type="button" onClick={() => { onChange(""); setImgError(false); }} className="mt-1 text-[10px] text-slate-400 hover:text-red-600" data-testid={`${testId}-remove`}>Remove</button>
        )}
        {error && <p className="text-[10px] text-red-600" data-testid={`${testId}-error`}>{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testId}>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testId}-file-input`} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid={`${testId}-camera-input`} />

      <div className="flex items-start gap-3">
        <div className="relative h-28 w-28 shrink-0 overflow-hidden border border-slate-200 bg-slate-50">
          {uploading ? (
            <div className="flex h-full w-full items-center justify-center text-[#002FA7]"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : src && !imgError ? (
            <img src={src} alt="preview" className="h-full w-full object-cover" onError={() => setImgError(true)} data-testid={`${testId}-preview`} />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-slate-300"><ImageOff className="h-6 w-6" /></div>
          )}
          {src && !uploading && (
            <button type="button" onClick={() => { onChange(""); setImgError(false); }} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white hover:bg-black/80" data-testid={`${testId}-remove`} aria-label="Remove image">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Button type="button" variant="outline" size="sm" className="rounded-sm justify-start" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`${testId}-pick-button`}>
            <Upload className="mr-2 h-4 w-4" /> Upload photo
          </Button>
          <Button type="button" variant="outline" size="sm" className="rounded-sm justify-start sm:hidden" disabled={uploading} onClick={() => cameraRef.current?.click()} data-testid={`${testId}-camera-button`}>
            <Camera className="mr-2 h-4 w-4" /> Take photo
          </Button>
          <p className="text-xs text-slate-400">JPG, PNG or WEBP · up to 15MB</p>
        </div>
      </div>
      {error && <p className="text-xs text-red-600" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
}
